import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { apiRequest } from "src/services/apiClient";
import { activateDemo, isDemoMode } from "./demoApi";
import { clearDemoState } from "./demoState";
import "./demoEntry.scss";

export default function DemoEntry() {
    const navigate = useNavigate();
    const [username, setUsername] = useState("");
    const [password, setPassword] = useState("");
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        // Always start the demo with a fresh seeded dataset for this tab/session.
        clearDemoState();
        if (isDemoMode()) {
            // Force a hard navigation so AuthProvider picks up demo doctor/praxis.
            window.location.replace("/main");
        }
    }, [navigate]);

    const canSubmit = useMemo(() => username.trim().length > 0 && password.length > 0 && !loading, [username, password, loading]);

    const submit = async () => {
        if (!canSubmit) return;
        setError(null);
        setLoading(true);
        try {
            const res = await apiRequest<{ ok: boolean; user: any }>("/demo/login", {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ username: username.trim(), password }),
            });

            const fullName = String(res.user?.fullName ?? "").trim();
            const nameParts = fullName.split(/\s+/).filter(Boolean);
            const firstName = nameParts.slice(0, -1).join(" ") || (nameParts[0] ?? "Demo");
            const lastName = nameParts.length > 1 ? nameParts[nameParts.length - 1] : "User";

            const specialty = String(res.user?.specialty ?? "").trim();
            const address = String(res.user?.address ?? "").trim();
            const email = String(res.user?.email ?? "").trim();

            const doctor = {
                id: "demo-doctor-1",
                firstName,
                lastName,
                salutation: "H",
                title: "",
                email: email || "demo@medinvoice.io",
                userRights: 3,
                lanr: "123456700",
                specialty: specialty ? [specialty] : ["Demo"],
                iban: "DE89370400440532013000",
                bic: "COBADEFFXXX",
                taxNumber: "123/456/78901",
                street: address || "—",
                plz: "",
                city: "",
            };

            const praxis = {
                id: "demo-praxis-1",
                name: fullName ? `Praxis ${fullName}` : "Demo Praxis",
                street: address || "—",
                plz: "",
                city: "",
                email: email || "demo@medinvoice.io",
                iban: "DE89370400440532013000",
                bic: "COBADEFFXXX",
                bank: "Demo Bank",
                specialization: specialty ? [specialty] : ["Demo"],
                businessPlan: 3,
                createdAt: new Date().toISOString(),
            };

            activateDemo({ doctor, praxis });
            // Force a hard navigation so AuthProvider picks up demo doctor/praxis.
            window.location.replace("/main");
        } catch (e: any) {
            setError(e?.body?.message ?? e?.message ?? "Login failed");
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="demo-entry">
            <div className="demo-entry__backdrop" />
            <div className="demo-entry__modal" role="dialog" aria-modal="true" aria-label="Demo login">
                <div className="demo-entry__title">Demo</div>
                <div className="demo-entry__sub">Bitte melden Sie sich mit Ihrem Demo-Zugang an.</div>

                {error ? <div className="demo-entry__error">{error}</div> : null}

                <label className="demo-entry__label">
                    <span>Username</span>
                    <input
                        value={username}
                        onChange={(e) => setUsername(e.target.value)}
                        autoComplete="username"
                        placeholder="demo-user"
                    />
                </label>
                <label className="demo-entry__label">
                    <span>Password</span>
                    <input
                        type="password"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        autoComplete="current-password"
                        placeholder="••••••••"
                        onKeyDown={(e) => {
                            if (e.key === "Enter") submit();
                        }}
                    />
                </label>

                <button className="demo-entry__btn" onClick={submit} disabled={!canSubmit}>
                    {loading ? "Checking…" : "Enter demo"}
                </button>
            </div>
        </div>
    );
}
